<script setup>
import { reactive, computed, watch, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(() => $stores.appData?.state?.data || {});

// Form sections
const couponsConfig = reactive({
  vipCouponsEnablingText: '',
  vipCouponsAllowed: false,
  vipAmountDone: 0,
  vipAmountRequired: 0,
  couponBannerPicture: ''
});

const subscription = reactive({
  couponSubscriptionPrice: 0
});

// Initialize all sections
watch(
  sourceData,
  (data) => {
    if (!data) return;

    const couponsData = data.coupons || {};

    Object.assign(couponsConfig, {
      vipCouponsEnablingText: couponsData.vipCouponsEnablingText || '',
      vipCouponsAllowed: couponsData.vipCouponsAllowed || false,
      vipAmountDone: couponsData.vipAmountDone || 0,
      vipAmountRequired: couponsData.vipAmountRequired || 0,
      couponBannerPicture: couponsData.couponBannerPicture || ''
    });

    Object.assign(subscription, {
      couponSubscriptionPrice: data.couponSubscriptionPrice || 0
    });
  },
  { immediate: true }
);

// Check if section has changes
const sectionChanged = (section, sectionKey) =>
  Object.keys(section).some((key) => {
    if (sectionKey === 'coupons') {
      return section[key] !== sourceData.value?.coupons?.[key];
    }
    return section[key] !== sourceData.value?.[key];
  });

const hasChanges = computed(
  () =>
    sectionChanged(couponsConfig, 'coupons') ||
    sectionChanged(subscription, 'subscription')
);

function save() {
  if (!hasChanges.value) return;

  const payload = {};

  // Check coupons section
  const couponsPayload = {};
  Object.keys(couponsConfig).forEach((key) => {
    if (couponsConfig[key] !== sourceData.value?.coupons?.[key]) {
      couponsPayload[key] = couponsConfig[key];
    }
  });
  if (Object.keys(couponsPayload).length > 0) {
    payload.coupons = couponsPayload;
  }

  // Check subscription section
  if (subscription.couponSubscriptionPrice !== sourceData.value?.couponSubscriptionPrice) {
    payload.couponSubscriptionPrice = subscription.couponSubscriptionPrice;
  }

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_GENERAL, payload);
}

function resetSection(section, sectionKey) {
  Object.keys(section).forEach((key) => {
    if (sectionKey === 'coupons') {
      section[key] =
        sourceData.value?.coupons?.[key] || (typeof section[key] === 'number' ? 0 : (typeof section[key] === 'boolean' ? false : ''));
    } else {
      section[key] =
        sourceData.value?.[key] || (typeof section[key] === 'number' ? 0 : '');
    }
  });
}

function resetAll() {
  resetSection(couponsConfig, 'coupons');
  resetSection(subscription, 'subscription');
}
</script>

<template>
  <div class="px-8 space-y-8">
    <!-- HEADER -->
    <div class="flex justify-between items-center sticky top-0 py-2 z-2">
      <h2 class="text-lg font-semibold">Paramètres des coupons</h2>
      <div class="flex gap-4" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="resetAll"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- SUBSCRIPTION PRICE -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">
          Abonnement aux coupons
        </h3>
        <button
          v-if="sectionChanged(subscription, 'subscription')"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(subscription, 'subscription')"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">Prix d'abonnement (XOF)</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="subscription.couponSubscriptionPrice"
          />
          <p class="text-xs text-gray-500 mt-1">
            Prix mensuel de l'abonnement pour accéder aux coupons premium
          </p>
        </div>
      </div>
    </section>

    <!-- VIP CONFIG -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">
          Configuration VIP
        </h3>
        <button
          v-if="sectionChanged(couponsConfig, 'coupons')"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(couponsConfig, 'coupons')"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="md:col-span-2">
          <label class="block mb-1 text-sm">Texte d'activation VIP</label>
          <textarea
            class="inputComponent h-20 resize-none"
            v-model="couponsConfig.vipCouponsEnablingText"
          ></textarea>
          <p class="text-xs text-gray-500 mt-1">
            Texte affiché pour expliquer comment activer les coupons VIP
          </p>
        </div>
        <div>
          <label class="block mb-1 text-sm">Coupons VIP autorisés</label>
          <select class="inputComponent" v-model="couponsConfig.vipCouponsAllowed">
            <option :value="true">Oui</option>
            <option :value="false">Non</option>
          </select>
        </div>
        <div>
          <label class="block mb-1 text-sm">Montant VIP atteint</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="couponsConfig.vipAmountDone"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Montant VIP requis</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="couponsConfig.vipAmountRequired"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Image bannière</label>
          <input
            class="inputComponent"
            v-model="couponsConfig.couponBannerPicture"
          />
          <p class="text-xs text-gray-500 mt-1">
            URL de l'image de bannière pour la section coupons
          </p>
        </div>
      </div>
    </section>

  </div>
</template>
